import { AnimationApp } from '@desygner/ui-editor-menus';
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { animation as config } from '@config';
import ReactAppBuilder from 'src/utils/react-app-builder';
import { capitalizeFirstLetter } from 'src/utils/string';

class AnimationView extends ReactAppBuilder {
  tabIndex = {
    page: 0,
    element: 1
  };

  constructor ({ appId, targetNodeSelector }) {
    super({ appId, targetNodeSelector });

    this.animationFactory = null;
    this.animationPreview = null;
  }

  closeApp () {
    super.closeApp(this.App);
  }

  mountApp () {
    this.animationFactory = this.animationFactory ?? INKIVE.AnimationFactory;
    this.animationPreview = document.querySelector('sidebar-animation-preview');

    super.mountApp(this.App);
  }

  async previewPageAnimation () {
    this.animationPreview.playCurrentAnimationOnCurrentPage();
  }

  async previewElementAnimation () {
    this.animationPreview.playCurrentAnimationOnCurrentPage(true);
  }

  roundVideoDuration (duration) {
    return Math.round(duration / 1000);
  }

  get labels () {
    return {
      animateElementsTogether: INKIVE.TranslateHelper.translate('top_submenu.animation.animate_elements_together'),
      animationDuration: INKIVE.TranslateHelper.translate('top_submenu.animation.in_out_duration'),
      applySameAnimation: INKIVE.TranslateHelper.translate('top_submenu.animation.apply_to_all_pages'),
      applySameDuration: INKIVE.TranslateHelper.translate('top_submenu.animation.apply_duration_to_all_pages'),
      beta: INKIVE.TranslateHelper.translate('top_submenu.animation.beta'),
      buttonRevertAnimation: INKIVE.TranslateHelper.translate('top_submenu.animation.revert_page_animation'),
      configurationTitle: INKIVE.TranslateHelper.translate('top_submenu.animation.duration_title'),
      durationDisclaimer: INKIVE.TranslateHelper.translate('top_submenu.animation.duration_disclaimer'),
      elementWithDefaultAnimation: INKIVE.TranslateHelper.translate('top_submenu.animation.element_with_no_animation'),
      groupedAnimation: INKIVE.TranslateHelper.translate('top_submenu.animation.grouped_animation'),
      pageDuration: INKIVE.TranslateHelper.translate('top_submenu.animation.page_duration_title'),
      secondsAbbreviation: INKIVE.TranslateHelper.translate('top_submenu.animation.seconds_abbreviation'),
      tabElement: INKIVE.TranslateHelper.translate('top_submenu.animation.element_animation'),
      tabPage: INKIVE.TranslateHelper.translate('top_submenu.animation.page_animation'),
      title: INKIVE.TranslateHelper.translate('top_submenu.animation.page_animations_title'),
      tooltipChangeAnimation: INKIVE.TranslateHelper.translate('top_submenu.animation.select_to_change_animation'),
      tooltipRemoveFromGroup: INKIVE.TranslateHelper.translate('top_submenu.animation.remove_from_group')
    };
  }

  get confirmationLabels () {
    return {
      title: INKIVE.TranslateHelper.translate('top_submenu.animation.confirm_split_title'),
      description: INKIVE.TranslateHelper.translate('top_submenu.animation.confirm_split_description'),
      remember: INKIVE.TranslateHelper.translate('top_submenu.animation.confirm_split_dont_ask_again'),
      confirmButton: INKIVE.TranslateHelper.translate('top_submenu.animation.confirm_split_submit'),
      cancelButton: INKIVE.TranslateHelper.translate('top_submenu.animation.confirm_split_cancel')
    };
  }

  mapProps () {
    const applySameAnimation = useSelector(state => state.metadata.animation?.apply_animation_all_pages);
    const applySameDuration = useSelector(state => state.metadata.animation?.apply_duration_all_pages);
    const selectedElementsFromState = useSelector(state => state.canvas.selected.elements);
    const selectedElements = useMemo(() =>
      this.animationFactory.getSelectedAnimatableElements().map(element => element.id)
    , [selectedElementsFromState]);
    const activePage = useSelector(state => state.editor.activePage);
    const pageMetadata = useSelector(state => state.metadata.animation?.pages?.[activePage]) ?? {};
    const [shouldAskToConfirmAnimation, setShouldAskToConfirmAnimation] = useState(true);
    const [activeTabIndex, setActiveTabIndex] = useState(selectedElements.length > 0 ? this.tabIndex.element : this.tabIndex.page);
    const [elementsFromCurrentPage, setElementsFromCurrentPage] = useState(this.animationFactory.getElementsFromCurrentPage());

    const configuration = {
      applySameAnimation: !!applySameAnimation,
      applySameDuration: !!applySameDuration,
      animateElementsTogether: !!pageMetadata.animateElementsTogether,
      pageDuration: (pageMetadata.pageDuration ?? config.defaultPageDuration) / 1000,
      inOutDuration: (pageMetadata.inOutDuration ?? config.defaultInOutDuration) / 1000,
      minimumPageDuration: (pageMetadata?.minimumPageDuration ?? 0) / 1000
    };

    useEffect(() => {
      setElementsFromCurrentPage(this.animationFactory.getElementsFromCurrentPage());
    }, [activePage]);

    useEffect(() => {
      // TODO: Remove this when all elements were reactive to the store
      const updateList = () => {
        const updatedElementsFromCurrentPage = this.animationFactory.getElementsFromCurrentPage();

        setElementsFromCurrentPage((previousState) => {
          if (JSON.stringify(updatedElementsFromCurrentPage) !== JSON.stringify(previousState)) {
            return updatedElementsFromCurrentPage;
          }

          return previousState;
        });
      };

      INKIVE.EditorFacade.on('history.change', updateList);
      INKIVE.EditorFacade.on('history.undo', updateList);
      INKIVE.EditorFacade.on('history.redo', updateList);

      INKIVE.DesignFacade.on('video.loadedmetadata', ({ id }) => {
        const element = document.getElementById(id);
        if (!element) return;

        const videoDuration = this.roundVideoDuration(Number(element.dataset.duration));

        if (videoDuration > configuration.minimumPageDuration) {
          this.animationFactory.applyConfiguration('minimumPageDuration', videoDuration, configuration);
        }
      });

      INKIVE.DesignFacade.on('predelete', (id) => {
        const element = document.getElementById(id);
        if (element && element.classList.contains('video')) {
          const videosDuration = [...document.querySelectorAll(
            `.element.video:not(#${element.id})`
          )].map(element => Number(element.dataset.duration));
          const maxDuration = videosDuration.length ? Math.max.apply(null, videosDuration) : 0;
          const duration = this.roundVideoDuration(maxDuration);

          this.animationFactory.applyConfiguration('minimumPageDuration', duration, configuration);
        }
      });
    }, []);

    return {
      ...configuration,
      animation: pageMetadata.animation ?? config.defaultAnimation,
      elements: elementsFromCurrentPage,
      elementAnimations: pageMetadata?.elementAnimations ?? {},
      groupAnimations: pageMetadata.groupAnimations ?? [],
      selectedElements,
      labels: this.labels,
      animations: config.effects.map(effect => ({
        key: effect,
        label: capitalizeFirstLetter(INKIVE.TranslateHelper.translate(`top_submenu.animation.${effect}`)),
        full: effect === 'no_animation'
      })),

      onAnimationChange: async (animation) => {
        if (selectedElements.length === 0 || activeTabIndex === this.tabIndex.page) {
          await INKIVE.AnimationFactory.setPageAnimation(animation, configuration.applySameAnimation);

          this.previewPageAnimation();
          return;
        }

        const applyElementAnimation = async () => {
          await INKIVE.AnimationFactory.setElementAnimation(selectedElements, animation);

          this.previewElementAnimation();
        };

        const hasGroupAnimation = this.animationFactory.hasGroupAnimation(selectedElements);
        const usesSameGroupAnimation = this.animationFactory.usesSameGroupAnimation(selectedElements, animation);

        // It shows confirmation modal when the animation set is different than the other elements in the group.
        if (!shouldAskToConfirmAnimation || !hasGroupAnimation || usesSameGroupAnimation) {
          applyElementAnimation();
          return;
        }

        INKIVE.EditorFacade.handle('confirm', 'show', {
          labels: this.confirmationLabels,
          onConfirm: (remember) => {
            remember && setShouldAskToConfirmAnimation(false);
            applyElementAnimation();
          },
          onCancel: () => INKIVE.EditorFacade.canvas.clearSelection()
        });
      },

      onAnimationReset: (elements) => {
        INKIVE.AnimationFactory.removeElementAnimation(elements);
      },

      onActiveTabChange: (index) => {
        setActiveTabIndex(index);
      },

      onConfigurationChange: (key, value) => {
        INKIVE.AnimationFactory.applyConfiguration(key, value, configuration);
      },

      onElementClick: (elements, remove) => {
        const uniqueIds = new Set();
        for (const id of elements) {
          const element = document.getElementById(id);
          uniqueIds.add(element.closest('.element.group')?.id ?? id);
        }

        INKIVE.EditorFacade.canvas.selectOnly(Array.from(uniqueIds).map(id => document.getElementById(id)));

        if (remove) {
          INKIVE.AnimationFactory.removeElementFromGroup(elements);
        }
      },

      onElementMouseEnter: function (elements) {
        const { canvas } = INKIVE.EditorFacade;

        elements.forEach(nodeId => {
          const node = document.getElementById(nodeId);
          const groupElement = node.closest('.element.group');

          node && canvas.selectorManager.requestSelector(groupElement ?? node);
        });
      },

      onElementMouseLeave: function (elements) {
        const { canvas } = INKIVE.EditorFacade;

        elements.forEach(nodeId => {
          const node = document.getElementById(nodeId);
          const groupElement = node.closest('.element.group');

          node && canvas.selectorManager.releaseSelector(groupElement ?? node);
        });
      }
    };
  }

  App = () => {
    const props = this.mapProps();

    return <AnimationApp {...props} />;
  };
}

export default new AnimationView({
  appId: 'react-animations-app',
  targetNodeSelector: '#tabAnimation.tab .animations-app'
});
